import EllipsisText from '@/components/EllipsisText';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import isLink from '../helpers/isLink';
import { PCell } from '../helpers/types';

function isLabel(label: unknown): label is string | string[] {
  return (!!label && typeof label === 'string') || (Array.isArray(label) && label.every(isLabel));
}

interface WithValue {
  value: string;
}

function isValueObject(obj: unknown): obj is WithValue {
  const tmp = obj as WithValue;
  return tmp && typeof tmp === 'object' && !!Object.keys(tmp).find((key) => key === 'value');
}

export default function DefaultCell<T>({
  cell,
  row,
  column,
  enableOrder,
  noTranslation,
}: PCell<T> & { enableOrder?: boolean; noTranslation?: boolean }) {
  const value = cell.getValue();
  const { transition, isDragging, setNodeRef, transform } = useSortable({
    id: column.id,
  });

  const component = useMemo(() => {
    if (isLink(value))
      return (
        <EllipsisText
          label={`${value.label || '-'}`}
          element={<Link to={value.link}>{value.label}</Link>}
          noTranslation={noTranslation}
        />
      );
    else if (isValueObject(value)) {
      return <EllipsisText label={`${value.value || '-'}`} noTranslation={noTranslation} />;
    } else if (isLabel(value)) {
      return <EllipsisText label={value} noTranslation={noTranslation} />;
    } else if (value === null || value === undefined || value === '') {
      return <EllipsisText label={isLabel(value) ? value : '-'} />;
    } else return <EllipsisText label={`${value}`} noTranslation={noTranslation} />;
  }, [value, noTranslation]);

  return (
    <div
      className="cell"
      key={`cell-${row.id}-${column.id}`}
      ref={setNodeRef}
      style={{
        opacity: enableOrder && isDragging ? 0.5 : undefined,
        transform: enableOrder ? CSS.Translate.toString(transform) : undefined,
        transition: enableOrder ? transition : undefined,
      }}
    >
      {component}
    </div>
  );
}
